// Chess ↔ the shared board-game views (../board): pieces from the FEN, the
// legal-move picker (from the server's own rules module), clocks, result
// text, lobby options and the rules guide.

import React from 'react';
import {
  legalMoveList, parseFen, sqName, turnOf, clockLeft, resultText, SEAT_NAMES,
  type ChessState, type MoveInfo,
} from '../../../../supabase/functions/games/chess.ts';
import type { BoardGameUI, PickRules, PieceSpec } from '../board/boardGame';
import { ChessPiece } from './ChessPieces';

const KINDS = ' pnbrqk';
const GLYPH: Record<string, string> = { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' };

// Legal moves per position (the picker asks every render).
const legalCache = new Map<string, MoveInfo[]>();
function legalOf(fen: string): MoveInfo[] {
  let l = legalCache.get(fen);
  if (!l) {
    l = legalMoveList(fen);
    if (legalCache.size > 64) legalCache.clear();
    legalCache.set(fen, l);
  }
  return l;
}

const pieceCache = new Map<string, PieceSpec[]>();
function piecesOf(fen: string): PieceSpec[] {
  let out = pieceCache.get(fen);
  if (out) return out;
  const p = parseFen(fen);
  out = [];
  for (let s = 0; s < 64; s++) {
    const v = p.b[s];
    if (v) out.push({ sq: s, kind: KINDS[v & 7], side: (v >> 3) as 0 | 1 });
  }
  if (pieceCache.size > 64) pieceCache.clear();
  pieceCache.set(fen, out);
  return out;
}

function rules(g: ChessState, seat: number): PickRules | null {
  if (g.result || turnOf(g) !== seat) return null;
  const legal = legalOf(g.fen);
  return {
    key: g.fen,
    starts: new Set(legal.map((m) => m.from)),
    next: (path) => (path.length === 1 ? [...new Set(legal.filter((m) => m.from === path[0]).map((m) => m.to))] : []),
    finish: (path) => {
      const [from, to] = path;
      const ms = legal.filter((m) => m.from === from && m.to === to);
      if (!ms.length) return null;
      if (ms.some((m) => m.promo)) return 'promo';
      return { type: 'move', from: sqName(from), to: sqName(to) };
    },
    promote: (path, piece) => ({ type: 'move', from: sqName(path[0]), to: sqName(path[1]), promo: piece }),
  };
}

export const chessUi: BoardGameUI<ChessState> = {
  options: [
    { key: 'level', label: 'Computer', values: [{ v: 'easy', label: 'Easy' }, { v: 'medium', label: 'Medium' }, { v: 'hard', label: 'Hard' }] },
    { key: 'clock', label: 'Clock', values: [{ v: 'none', label: 'None' }, { v: '10', label: '10 min' }, { v: '5', label: '5 min' }] },
  ],
  defaults: { level: 'medium', clock: 'none' },
  pieces: (g) => piecesOf(g.fen),
  last: (g) => (g.last ? [g.last.from, g.last.to] : []),
  check: (g) => {
    if (!g.check) return null;
    const p = parseFen(g.fen);
    return p.kings[p.side];
  },
  turn: (g) => (g.result ? null : turnOf(g)),
  rules,
  moves: (g) => g.moves,
  result: (g) => (g.result ? { winner: g.result.winner, text: resultText(g) } : null),
  drawOffer: (g) => g.drawOffer,
  clocks: (g, now) => (g.clock ? [clockLeft(g, 0, now) ?? 0, clockLeft(g, 1, now) ?? 0] : null),
  promoChoices: [{ v: 'q', label: 'Queen' }, { v: 'r', label: 'Rook' }, { v: 'b', label: 'Bishop' }, { v: 'n', label: 'Knight' }],
  // U+FE0E keeps ♟ a text glyph (not an emoji) on phones.
  glyph: (p) => <span className={`gx-bd-chess side-${p.side}`}>{GLYPH[p.kind]}{'︎'}</span>,
  Piece: ChessPiece,
};

export { SEAT_NAMES };

export const CHESS_TUTORIAL = { url: 'https://www.youtube.com/watch?v=OCSbzArwB10', title: 'How To Play Chess: The Ultimate Beginner Guide — GothamChess' };

export const CHESS_RULES: { title: string; lines: string[] }[] = [
  {
    title: 'The board',
    lines: [
      'Two players: White (moves first) and Black. Each player sees their own side nearest them.',
      'Tap one of your pieces, then a highlighted square. In VR, poke or pinch the piece on the table, then the square.',
    ],
  },
  {
    title: 'How the pieces move',
    lines: [
      'King: one square any way. Queen: any distance in a straight line or diagonal. Rook: straight lines. Bishop: diagonals. Knight: an L-shape, jumping over pieces.',
      'Pawn: one square forward (two from its starting square), captures one square diagonally forward.',
      'Capture by moving onto an opponent\'s piece.',
    ],
  },
  {
    title: 'Special moves',
    lines: [
      'Castling: king two squares toward a rook, the rook jumps over. Only if neither has moved, the squares between are empty, and the king is not in check, does not pass through check and does not land in check.',
      'En passant: a pawn that just moved two squares can be captured by an enemy pawn beside it as if it had moved one — only on the very next move.',
      'Promotion: a pawn reaching the last row becomes a queen, rook, bishop or knight — you choose.',
    ],
  },
  {
    title: 'Winning and draws',
    lines: [
      'Check: the king is attacked — you must get out of check. Checkmate: no way out — that player loses.',
      'Draws: stalemate (no legal move but not in check), the same position three times, 50 moves each without a pawn move or capture, not enough pieces left to mate, or a draw offer accepted.',
      'Resign any time. With a clock, running out of time loses (a draw if the opponent can\'t possibly mate). Going 10 minutes without moving forfeits.',
    ],
  },
  {
    title: 'Playing the computer',
    lines: ['Choose Easy, Medium or Hard in the lobby, then "Fill with computer players" (or "Play computer" in VR). Clock options: none, 10 or 5 minutes each.'],
  },
];
